// src/components/Layout.jsx
import { Link, Outlet, useLocation } from "react-router-dom";
import {
  Home,
  ArrowRight,
  Clock,
  DollarSign,
  HelpCircle,
  Settings as SettingsIcon,
  BookOpen,
  Menu,
  LogOut,
  User,
  X,
} from "lucide-react";
import LogoFull from "../assets/logo_full.png";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import OpenInAppBanner from "@/components/OpenInAppBanner";
import api from "../api";
import { groupTradesByWindow } from "../utils/tradeWindows";
import { getVerifiedWindowComparison, isNewestKnownCompletedWindow, normalizedWindowKey } from "../utils/windowCloseComparison";
import { forceSyncFromRevenueCat, isNative } from "../iap";
import { Browser } from "@capacitor/browser";
import { Share } from "@capacitor/share";
import { afTrack } from "../analytics/appsflyer";

// ✅ Store links
const PLAY_STORE_REVIEW_URL =
  "https://play.google.com/store/apps/details?id=com.satstacker.app";

const APP_STORE_REVIEW_URL =
  "https://apps.apple.com/app/id6748700654?action=write-review";

const FALLBACK_URL = "https://satstacker.app/download";

const buildShareUrl = (userInfo) => {
  const code =
    userInfo?.ref_code ||
    userInfo?.referral_code ||
    userInfo?.id ||
    "";

  return `${FALLBACK_URL}?ref=${encodeURIComponent(code)}`;
};

// Win popup threshold.
// Keep this separate from review eligibility.
const MIN_EXTRA_PCT = 0.015; // 1.5%

// Review prompt policy.
//
// We only ask during a verified positive completed-window moment,
// but the user no longer needs THREE large wins.
//
// Two completed Smart Timing windows is enough product experience
// before we consider asking.
const REVIEW_MIN_COMPLETED_WINDOWS = 2;

// "Later" should actually mean later — not months.
const REVIEW_SNOOZE_DAYS = 30;

// If someone deliberately taps "Leave a review", don't ask them
// again for a long time.
const REVIEW_AFTER_REVIEW_TAP_DAYS = 180;

// Keep v1 so existing users keep any existing snooze state.
// Old attempted/lastPrompt keys can remain in localStorage;
// the new logic simply ignores them.
const REVIEW_KEY_PREFIX = "ss_review_v1_";

// ✅ DEBUG FLAGS (turn off before release)
const DEBUG_FORCE_WIN_POPUP = false;
const DEBUG_FORCE_REVIEW_NUDGE = false;

const HEADER_H = 56; // px (h-14)

const NAV = [
  { label: "Dashboard", path: "/dashboard", icon: Home },
  { label: "Buy Plan", path: "/plans", icon: ArrowRight },
  { label: "Trade History", path: "/trades", icon: Clock },
  { label: "Pricing", path: "/pricing", icon: DollarSign },
  { label: "FAQ", path: "/faq", icon: HelpCircle },
  { label: "Resources", path: "/resources", icon: BookOpen },
  { label: "Settings", path: "/settings", icon: SettingsIcon },
];

function getStoreReviewLink() {
  const ua = navigator.userAgent || "";

  const isAndroid = /Android/i.test(ua);
  const isIOS = /iPhone|iPad|iPod/i.test(ua);

  if (isAndroid) return PLAY_STORE_REVIEW_URL;
  if (isIOS) return APP_STORE_REVIEW_URL;

  return FALLBACK_URL;
}

function formatPct(x) {
  if (!Number.isFinite(x)) return "0.00%";

  return `${(x * 100).toFixed(2)}%`;
}

function msDays(d) {
  return d * 24 * 60 * 60 * 1000;
}

function getReviewKeys(uid) {
  return {
    snoozeUntil:
      `${REVIEW_KEY_PREFIX}${uid}_snooze_until_ms`,
  };
}

function shouldPromptReview(
  uid,
  completedWindows
) {
  try {
    if (!uid) return false;

    const completed =
      Number(completedWindows || 0);

    if (
      !Number.isFinite(completed) ||
      completed < REVIEW_MIN_COMPLETED_WINDOWS
    ) {
      return false;
    }

    const k = getReviewKeys(uid);

    const snoozeUntil = parseInt(
      localStorage.getItem(k.snoozeUntil) || "0",
      10
    );

    if (
      Number.isFinite(snoozeUntil) &&
      Date.now() < snoozeUntil
    ) {
      return false;
    }

    return true;
  } catch {
    return false;
  }
}

function snoozeReview(
  uid,
  days = REVIEW_SNOOZE_DAYS
) {
  try {
    const k = getReviewKeys(uid);

    localStorage.setItem(
      k.snoozeUntil,
      String(
        Date.now() + msDays(days)
      )
    );
  } catch {}
}

async function openStoreReviewPage() {
  const url = getStoreReviewLink();

  try {
    if (isNative()) {
      await Browser.open({ url });
      return;
    }

    window.open(
      url,
      "_blank",
      "noopener,noreferrer"
    );
  } catch (error) {
    console.warn(
      "[Review] could not open store",
      error
    );
  }
}

function formatTimeOnly(dateLike) {
  // Ensure UTC ISO strings (no "Z" suffix) are treated as UTC, not local time
  let raw = dateLike;
  if (typeof raw === "string" && !raw.endsWith("Z") && !/[+-]\d{2}:\d{2}$/.test(raw)) {
    raw = raw + "Z"; // append Z so the browser parses it as UTC
  }
  const d = raw instanceof Date ? raw : new Date(raw);
  return d.toLocaleTimeString([], {
    hour: "numeric",
    minute: "2-digit",
  });
}

function Sidebar({ className = "", onClose = () => {}, userInfo, onLogout }) {
  const { pathname } = useLocation();
  const isActive = (p) => pathname.startsWith(p);

  return (
    <aside
      className={`w-64 bg-black text-white flex flex-col border-r border-border ${className}`}
      onClick={onClose}
    >
      <div
        className="flex flex-col items-center pb-8"
        style={{
          paddingTop: "calc(var(--safe-area-inset-top, env(safe-area-inset-top, 0px)) + 36px)",
        }}
      >
        <img
          src={LogoFull}
          alt="SatStacker logo"
          className="w-20"
          draggable={false}
        />
      </div>

      <nav className="px-3 space-y-1" onClick={(e) => e.stopPropagation()}>
        {NAV.map(({ label, path, icon: Icon }) => (
          <Link
            key={path}
            to={path}
            onClick={onClose}
            className={`flex items-center gap-3 px-4 py-3 rounded-lg transition
              ${isActive(path) ? "bg-zinc-900 text-orange-500" : "text-zinc-300 hover:bg-zinc-800 hover:text-white"}`}
          >
            <Icon className="w-5 h-5 shrink-0" />
            <span className="font-medium">{label}</span>
          </Link>
        ))}
      </nav>

      <div className="mt-8 px-3 py-4 border-t border-border">
        {userInfo?.id ? (
          <button
            onClick={onLogout}
            className="flex items-center gap-3 w-full px-4 py-3 rounded-lg bg-zinc-800 hover:bg-zinc-700 transition"
          >
            <LogOut className="w-5 h-5 shrink-0" />
            <span className="font-medium">Log&nbsp;out</span>
          </button>
        ) : (
          <Link
            to="/login"
            className="flex items-center gap-3 w-full px-4 py-3 rounded-lg bg-orange-500 text-white hover:bg-orange-600 active:bg-orange-700 transition"
          >
            <User className="w-5 h-5 shrink-0" />
            <span className="font-medium">Log&nbsp;in</span>
          </Link>
        )}
      </div>
    </aside>
  );
}

export default function Layout({ userInfo, onLogout }) {
  const [open, setOpen] = useState(false);

  // Win popup
  const [winOpen, setWinOpen] = useState(false);
  const [win, setWin] = useState(null);

  // Review popup (separate modal)
  const [reviewOpen, setReviewOpen] = useState(false);
  const [pendingReview, setPendingReview] = useState(false);

  const { pathname } = useLocation();
  const routeTopRef = useRef(null);

  useEffect(() => {
    const resetRouteScroll = () => {
      // scrollIntoView is intentional here: unlike window.scrollTo(), it also
      // resets whichever ancestor is actually acting as the scroll container.
      routeTopRef.current?.scrollIntoView({
        block: "start",
        inline: "nearest",
        behavior: "auto",
      });

      // Browser/document fallback.
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    };

    // Run after the new route has committed, then once more on the next frame
    // in case layout/font/image work shifts the page during navigation.
    const frame1 = window.requestAnimationFrame(() => {
      resetRouteScroll();
      window.requestAnimationFrame(resetRouteScroll);
    });

    return () => window.cancelAnimationFrame(frame1);
  }, [pathname]);

  const shareUrl = useMemo(() => buildShareUrl(userInfo), [userInfo]);

  const deeplink =
    typeof window !== "undefined"
      ? window.location.pathname + window.location.search + window.location.hash
      : "/";

  const storageKeyPrefix = useMemo(() => {
    const uid = userInfo?.id != null ? String(userInfo.id) : "anon";
    return `ss_latest_entry_seen_v1_${uid}_`;
  }, [userInfo?.id]);

  const windowStorageKeyPrefix = useMemo(() => {
    const uid = userInfo?.id != null ? String(userInfo.id) : "anon";
    return `ss_latest_window_result_seen_v1_${uid}_`;
  }, [userInfo?.id]);

  const showLatestWindowWin = useCallback(async () => {
    if (!userInfo?.id) return false;

    try {
      const res = await api.get("/trades/latest-window-result");
      const w = res?.data?.window;

      if (!w) {
        console.log("[WINDOW WIN] no completed winning window");
        return false;
      }

      const windowKey = w?.key ? String(w.key) : "";

      if (!windowKey) {
        console.log("[WINDOW WIN] missing window key", w);
        return false;
      }

      try {
        if (
          localStorage.getItem(
            windowStorageKeyPrefix + windowKey
          ) === "1"
        ) {
          console.log(
            "[WINDOW WIN] already seen",
            windowKey
          );
          return false;
        }
      } catch {}

      // The lightweight win endpoint is only a candidate. Cross-check the
      // full scheduled-close quote, fee-adjusted sats, and COMPLETE recorded
      // trade group before showing a celebration or counting a review win.
      const [performanceResponse, tradesResponse] = await Promise.all([
        api.get("/smart-timing/performance"),
        api.get("/trades"),
      ]);
      const performance = performanceResponse?.data;
      const windows = performance?.windows || [];
      const candidateKey = normalizedWindowKey(w.plan_id, w.window_start);
      const newestVerified = windows[0];
      if (!newestVerified ||
          normalizedWindowKey(newestVerified.plan_id, newestVerified.window_start) !== candidateKey) {
        return false;
      }
      const groups = groupTradesByWindow(tradesResponse?.data || []);
      if (!isNewestKnownCompletedWindow(groups, newestVerified)) return false;
      const matchingGroup = groups.find(
        (group) => normalizedWindowKey(group.planId, group.windowStart) === candidateKey
      );
      const verified = getVerifiedWindowComparison(matchingGroup, newestVerified);
      if (!verified || verified.pct <= 0 ||
          verified.gainPct < MIN_EXTRA_PCT * 100 || verified.deltaSats <= 0 ||
          Math.abs(Number(w.gain_pct) * 100 - verified.gainPct) > 0.01 ||
          Math.abs(Number(w.extra_sats) - verified.deltaSats) > 2) {
        console.log("[WINDOW WIN] candidate cannot be independently verified", windowKey);
        return false;
      }
      // Popup helpers take a fraction; all normal performance API and card
      // fields take percentage points. Keep this conversion explicit.
      const extraPct = verified.gainPct / 100;
      const extraSats = verified.deltaSats;
      const totalSats = verified.actualSats;
      const baselineSats = verified.scheduledSats;

      setWin({
        kind: "window",

        // No individual trade owns a window popup.
        tradeId: null,

        tradeKey: windowKey,

        totalSats,
        extraSats,
        extraPct,
        baselineSats,

        tradeCount: verified.tradeCount,

        windowStart:
          w.window_start || null,

        executedAt: w.completed_at
          ? formatTimeOnly(w.completed_at)
          : "—",
      });

      setWinOpen(true);

      const uid =
        userInfo?.id != null
          ? String(userInfo.id)
          : null;

      const completedWindows = Number(
        performance?.summary?.completed_windows ??
        windows.length ??
        0
      );

      if (
        uid &&
        isNative() &&
        shouldPromptReview(
          uid,
          completedWindows
        )
      ) {
        setPendingReview(true);

        afTrack(
          "review_nudge_eligible",
          {
            completed_windows:
              completedWindows,

            trigger_gain_pct:
              Number(
                verified.gainPct || 0
              ),
          }
        );
      }

      afTrack("window_win_popup_shown", {
        extra_pct: extraPct,
        trade_count: Number(
          verified.tradeCount
        ),
      });

      return true;
    } catch (err) {
      console.log(
        "[WINDOW WIN] caught error:",
        err
      );
      return false;
    }
  }, [
    userInfo?.id,
    windowStorageKeyPrefix,
  ]);

  const dismissWin = async () => {
    const dismissedKind =
      win?.kind || "entry";

    const dismissedTradeKey =
      win?.tradeKey || null;

    const dismissedTradeId =
      win?.tradeId || null;

    if (dismissedTradeKey) {
      try {
        const prefix =
          dismissedKind === "window"
            ? windowStorageKeyPrefix
            : storageKeyPrefix;

        localStorage.setItem(
          prefix + dismissedTradeKey,
          "1"
        );
      } catch {}
    }

    // Immediate Smart Timing entries still use the trade-level
    // backend seen flag. Completed-window wins are keyed locally
    // because no single trade owns the whole window result.
    if (
      dismissedKind === "entry" &&
      dismissedTradeId
    ) {
      try {
        await api.post(
          `/trades/${dismissedTradeId}/mark-win-seen`
        );
      } catch {}
    }

    setWinOpen(false);
    setWin(null);

    if (pendingReview) {
      setPendingReview(false);

      setTimeout(() => {
        setReviewOpen(true);

        afTrack(
          "review_nudge_shown",
          {
            trigger:
              "completed_window_win",
          }
        );
      }, 500);
    }
  };

  const askForReview = async () => {
    const uid =
      userInfo?.id != null
        ? String(userInfo.id)
        : null;

    if (!uid) {
      setReviewOpen(false);
      return;
    }

    // They intentionally chose to review.
    // Give them a long cooldown before
    // SatStacker ever asks again.
    snoozeReview(
      uid,
      REVIEW_AFTER_REVIEW_TAP_DAYS
    );

    afTrack(
      "review_nudge_accepted",
      {
        trigger:
          "completed_window_win",
      }
    );

    setReviewOpen(false);

    await openStoreReviewPage();
  };

  const notNowReview = () => {
    const uid =
      userInfo?.id != null
        ? String(userInfo.id)
        : null;

    if (uid) {
      snoozeReview(
        uid,
        REVIEW_SNOOZE_DAYS
      );
    }

    afTrack(
      "review_nudge_later",
      {
        trigger:
          "completed_window_win",
      }
    );

    setReviewOpen(false);
  };

  const shareWin = async () => {
    if (!win || win.kind !== "window") return;

    const msg =
      `Smart Timing got me ${formatPct(win.extraPct)} more Bitcoin than my scheduled DCA buy on SatStacker ⚡\n` +
      `Same schedule. Same budget. Smarter timing.\n`;

    let shared = false;

    try {
      if (isNative()) {
        await Share.share({
          title: "SatStacker Smart Timing",
          text: msg,
          url: shareUrl,
          dialogTitle: "Share SatStacker",
        });
        shared = true;
      } else if (navigator.share) {
        await navigator.share({
          title: "SatStacker Smart Timing",
          text: msg,
          url: shareUrl,
        });
        shared = true;
      } else if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(
          `${msg}\n${shareUrl}`
        );
        shared = true;
        alert("Copied share text to clipboard!");
      } else {
        window.prompt(
          "Copy this:",
          `${msg}\n${shareUrl}`
        );
      }

      if (shared) {
        afTrack(
          "win_popup_shared",
          { extra_pct: win.extraPct }
        );
      }
    } catch {
      // cancelled or failed
    } finally {
      dismissWin();
    }
  };

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      if (!userInfo?.id) {
        console.log("[SMART TIMING] bail: no userInfo");
        return;
      }

      if (DEBUG_FORCE_WIN_POPUP) {
        const tradeKey = "debug-entry";

        try {
          localStorage.removeItem(
            storageKeyPrefix + tradeKey
          );
        } catch {}

        if (cancelled) return;

        setWin({
          kind: "entry",
          tradeId: null,
          tradeKey,
          entryPct: 0.024,
          windowOpenPrice: 65000,
          executionPrice: 63440,
          executedAt: formatTimeOnly(new Date()),
        });

        setWinOpen(true);
        return;
      }

      try {
        // A finalized completed-window result takes priority over
        // an in-progress entry celebration.
        const showedWindow =
          await showLatestWindowWin();

        if (showedWindow || cancelled) {
          return;
        }

        const res = await api.get(
          "/trades/latest-entry"
        );

        const entry = res?.data?.entry;

        if (!entry) {
          console.log(
            "[SMART ENTRY] no eligible open-window entry"
          );
          return;
        }

        const tradeKey =
          entry?.id != null
            ? String(entry.id)
            : "";

        if (!tradeKey) {
          console.log(
            "[SMART ENTRY] missing trade key",
            entry
          );
          return;
        }

        try {
          if (
            localStorage.getItem(
              storageKeyPrefix + tradeKey
            ) === "1"
          ) {
            try {
              await api.post(
                `/trades/${entry.id}/mark-win-seen`
              );
            } catch {}

            return;
          }
        } catch {}

        const entryPct = Number(
          entry.entry_pct ?? 0
        );

        const windowOpenPrice = Number(
          entry.window_open_price ?? 0
        );

        const executionPrice = Number(
          entry.execution_price ?? 0
        );

        if (
          !Number.isFinite(entryPct) ||
          !Number.isFinite(windowOpenPrice) ||
          !Number.isFinite(executionPrice) ||
          entryPct < MIN_EXTRA_PCT ||
          windowOpenPrice <= 0 ||
          executionPrice <= 0
        ) {
          console.log(
            "[SMART ENTRY] invalid fields",
            entry
          );
          return;
        }

        if (cancelled) return;

        setWin({
          kind: "entry",
          tradeId: entry.id,
          tradeKey,
          entryPct,
          windowOpenPrice,
          executionPrice,
          executedAt: entry.executed_at
            ? formatTimeOnly(
                entry.executed_at
              )
            : "—",
        });

        setWinOpen(true);

        afTrack(
          "smart_timing_entry_popup_shown",
          {
            entry_pct: entryPct,
          }
        );
      } catch (err) {
        console.log(
          "[SMART TIMING] popup check error:",
          err
        );
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [
    userInfo?.id,
    storageKeyPrefix,
    showLatestWindowWin,
  ]);

  useEffect(() => {
    const onOrientationFix = () => {
      window.setTimeout(() => {
        try {
          document.documentElement.style.width = "100%";
          document.body.style.width = "100%";
          window.dispatchEvent(new Event("resize"));
        } catch {}
      }, 250);
    };

    window.addEventListener("orientationchange", onOrientationFix);
    window.addEventListener("resize", onOrientationFix);

    return () => {
      window.removeEventListener("orientationchange", onOrientationFix);
      window.removeEventListener("resize", onOrientationFix);
    };
  }, []);

  return (
    <div className="flex w-full max-w-full overflow-x-hidden bg-slate-dark min-h-screen">
      {/* Mobile Backdrop */}
      {open && (
        <div
          className="fixed inset-0 bg-black/60 z-50 md:hidden"
          onClick={() => setOpen(false)}
        />
      )}

      {/* Sidebar */}
      <Sidebar
        className={`fixed inset-y-0 left-0 w-64 bg-black text-white
          transform transition-transform duration-200 ease-in-out
          z-[60] flex flex-col overflow-y-auto
          ${open ? "translate-x-0" : "-translate-x-full"}
          md:static md:z-auto md:translate-x-0 md:flex`}
        onClose={() => setOpen(false)}
        userInfo={userInfo}
        onLogout={onLogout}
      />

      {/* Main */}
      <main
        className="flex-1 min-w-0 w-full max-w-full overflow-x-hidden px-4 md:px-10 lg:px-12"
        style={{
          paddingTop: `calc(var(--safe-area-inset-top, env(safe-area-inset-top, 0px)) + ${HEADER_H}px + 16px)`,
          paddingBottom: `calc(var(--safe-area-inset-bottom, env(safe-area-inset-bottom, 0px)) + 24px)`,
        }}
      >
        <OpenInAppBanner deeplink={deeplink} />

        <header
          className="md:hidden fixed left-0 right-0 top-0 z-40 
          bg-slate-dark/92 backdrop-blur 
          shadow-[0_1px_8px_rgba(0,0,0,0.4)]"
          style={{
            paddingTop: "var(--safe-area-inset-top, env(safe-area-inset-top, 0px))",
            height: `calc(var(--safe-area-inset-top, env(safe-area-inset-top, 0px)) + ${HEADER_H}px)`,
          }}
        >
          <div className="h-14 px-4 flex items-center gap-3 text-white">
            <button
              onClick={() => setOpen(true)}
              className="p-2 rounded-md hover:bg-zinc-800"
              aria-label="Open navigation"
            >
              <Menu size={24} />
            </button>

            <h1 className="text-xl font-bold tracking-tight">SatStacker</h1>
          </div>
        </header>

        <div className="w-full max-w-6xl mx-auto space-y-6 overflow-x-hidden">
          {/* ✅ SMART TIMING RESULT / ENTRY MODAL */}
          {winOpen && win && (
            <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
              <div className="absolute inset-0 bg-black/70" onClick={dismissWin} />

              <div className="relative w-full max-w-md rounded-2xl bg-slate-900 border border-slate-700 shadow-xl p-5">
                {win.kind === "window" ? (
                  <>
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="text-white text-lg font-semibold">
                          Smart Timing Win ⚡
                        </div>

                        <div className="text-slate-300 text-sm mt-1">
                          You got{" "}
                          <span className="text-green-400 font-semibold">
                            {formatPct(win.extraPct)}
                          </span>{" "}
                          more Bitcoin vs your scheduled DCA buy
                        </div>
                      </div>

                      <button
                        className="text-slate-300 hover:text-white"
                        onClick={dismissWin}
                        aria-label="Close"
                      >
                        <X size={18} />
                      </button>
                    </div>

                    <div className="mt-4 text-slate-200 text-sm space-y-2">
                      <div className="rounded-xl border border-slate-700 bg-slate-950/50 px-4 py-3">
                        <div className="text-slate-400 text-xs uppercase tracking-wide">
                          Completed-window result
                        </div>

                        <div className="mt-1 text-3xl font-bold text-green-400">
                          {formatPct(win.extraPct)}
                        </div>

                        <div className="mt-1 text-slate-300 text-sm">
                          +{Math.round(
                            Number(win.extraSats || 0)
                          ).toLocaleString()} sats vs scheduled DCA
                          {win.tradeCount > 1
                            ? ` across ${win.tradeCount} buys`
                            : ""}
                        </div>
                      </div>

                      <div className="text-slate-500 text-xs mt-2">
                        Same schedule. Same budget. Smarter timing.
                      </div>

                      <div className="text-slate-500 text-xs">
                        {win.executedAt}
                      </div>
                    </div>

                    <p className="mt-4 text-xs text-slate-400 text-center">
                      Share your result. If a friend signs up, you’ll get 1 free month of Smart Timing.
                    </p>

                    <div className="mt-5 flex gap-2">
                      <button
                        onClick={shareWin}
                        className="flex-1 rounded-xl bg-orange-500 hover:bg-orange-400 text-white py-2 font-semibold"
                      >
                        Share
                      </button>

                      <button
                        onClick={dismissWin}
                        className="flex-1 rounded-xl bg-slate-700 hover:bg-slate-600 text-white py-2 font-semibold"
                      >
                        Not now
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="text-white text-lg font-semibold">
                          Smart Timing found an entry ⚡
                        </div>

                        <div className="text-slate-300 text-sm mt-1">
                          Your planned buy executed{" "}
                          <span className="text-orange-400 font-semibold">
                            {formatPct(win.entryPct)}
                          </span>{" "}
                          below the start of this buy window.
                        </div>
                      </div>

                      <button
                        className="text-slate-300 hover:text-white"
                        onClick={dismissWin}
                        aria-label="Close"
                      >
                        <X size={18} />
                      </button>
                    </div>

                    <div className="mt-4 rounded-xl border border-slate-700 bg-slate-950/50 px-4 py-3">
                      <div className="flex items-center justify-between gap-4 text-sm">
                        <span className="text-slate-400">
                          Window started
                        </span>
                        <span className="text-slate-300 tabular-nums">
                          ${Math.round(
                            Number(win.windowOpenPrice || 0)
                          ).toLocaleString()}
                        </span>
                      </div>

                      <div className="mt-3 flex items-center justify-between gap-4 text-sm">
                        <span className="text-slate-400">
                          Smart Timing entry
                        </span>
                        <span className="font-semibold text-orange-400 tabular-nums">
                          ${Math.round(
                            Number(win.executionPrice || 0)
                          ).toLocaleString()}
                        </span>
                      </div>
                    </div>

                    <p className="mt-4 text-xs text-slate-400 leading-relaxed">
                      Your final Smart Timing result will be available when this buy window is complete.
                    </p>

                    <div className="mt-5">
                      <button
                        onClick={dismissWin}
                        className="w-full rounded-xl bg-orange-500 hover:bg-orange-400 text-white py-2 font-semibold"
                      >
                        Got it
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          {/* ✅ REVIEW MODAL (SEPARATE) */}
          {reviewOpen && (
            <div className="fixed inset-0 z-[80] flex items-center justify-center p-4">
              <div className="absolute inset-0 bg-black/70" onClick={notNowReview} />
              <div className="relative w-full max-w-md rounded-2xl bg-slate-900 border border-slate-700 shadow-xl p-5">
                <div className="flex items-start justify-between gap-3">
                  <div className="text-white text-lg font-semibold">
                    Enjoying SatStacker?
                  </div>
                  <button
                    className="text-slate-300 hover:text-white"
                    onClick={notNowReview}
                    aria-label="Close"
                  >
                    <X size={18} />
                  </button>
                </div>

                <div className="text-slate-300 text-sm mt-2 leading-relaxed">
                  If Smart Timing has been useful, a quick review helps
                  other Bitcoiners discover SatStacker and helps us keep
                  improving it.
                </div>

                <div className="mt-5 flex gap-2">
                  <button
                    onClick={askForReview}
                    className="flex-1 rounded-xl bg-orange-500 hover:bg-orange-400 text-white py-2 font-semibold"
                  >
                    Leave a review
                  </button>
                  <button
                    onClick={notNowReview}
                    className="flex-1 rounded-xl bg-slate-700 hover:bg-slate-600 text-white py-2 font-semibold"
                  >
                    Later
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Route scroll sentinel — keep immediately before Outlet */}
          <div
            ref={routeTopRef}
            aria-hidden="true"
            style={{ height: 0, width: "100%", overflow: "hidden" }}
          />
          <Outlet />
        </div>
      </main>
    </div>
  );
}